import { GranularityType } from '@/hooks'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type DataEntry = Record<string, unknown> & { date: string }

export type GranularityIndices = {
    startIndex: number
    endIndex: number
}

export type GranularityDates = {
    startDate: string
    endDate: string
}

export const calculateGranularityIndices = (
    processedData: DataEntry[],
    granularity?: GranularityType
): GranularityIndices => {
    const dataLength = processedData.length

    if (dataLength === 0 || !granularity) {
        return { startIndex: 0, endIndex: dataLength - 1 }
    }

    const lastDate = dayjs.utc(processedData[dataLength - 1]?.date)
    if (!lastDate.isValid()) {
        return { startIndex: 0, endIndex: dataLength - 1 }
    }

    // Calculate start date based on granularity
    let startDate: dayjs.Dayjs
    switch (granularity) {
        case '1_day':
            startDate = lastDate.subtract(1, 'day')
            break
        case '1_week':
            startDate = lastDate.subtract(1, 'week')
            break
        case '1_month':
            startDate = lastDate.subtract(1, 'month')
            break
        case '2_months':
            startDate = lastDate.subtract(2, 'month')
            break
        default:
            return { startIndex: 0, endIndex: dataLength - 1 }
    }

    // Find the index of the start date
    let startIndex = 0
    const startTimestamp = startDate.valueOf()
    for (let i = 0; i < dataLength; i++) {
        const currentDate = dayjs.utc(processedData[i]?.date)
        if (currentDate.isValid() && currentDate.valueOf() >= startTimestamp) {
            startIndex = i
            break
        }
    }

    return {
        startIndex,
        endIndex: dataLength - 1,
    }
}

/**
 * Calculates the start and end dates based on granularity.
 * If endDate is provided, calculates from that date; otherwise uses timelineEndDate or the last data point.
 * The start date is calculated by subtracting the granularity period from the end date.
 * If there's insufficient data for the desired granularity, adjusts the end date to show maximum available data.
 */
export const calculateGranularityDates = (
    processedData: DataEntry[],
    granularity?: GranularityType,
    endDate?: string,
    timelineStartDate?: string,
    timelineEndDate?: string
): GranularityDates | null => {
    const dataLength = processedData.length

    if (dataLength === 0) {
        return null
    }

    // Use provided endDate, then timelineEndDate, then fall back to last data point
    let targetEndDate: dayjs.Dayjs
    if (endDate) {
        targetEndDate = dayjs.utc(endDate)
        if (!targetEndDate.isValid()) {
            // If provided endDate is invalid, try timelineEndDate
            if (timelineEndDate) {
                targetEndDate = dayjs.utc(timelineEndDate)
            }
            // If still invalid, fall back to last data point
            if (!targetEndDate.isValid()) {
                targetEndDate = dayjs.utc(processedData[dataLength - 1]?.date)
            }
        }
    } else if (timelineEndDate) {
        targetEndDate = dayjs.utc(timelineEndDate)
        if (!targetEndDate.isValid()) {
            targetEndDate = dayjs.utc(processedData[dataLength - 1]?.date)
        }
    } else {
        targetEndDate = dayjs.utc(processedData[dataLength - 1]?.date)
    }

    if (!targetEndDate.isValid()) {
        return null
    }

    const firstDataDate = dayjs.utc(processedData[0]?.date)
    if (!firstDataDate.isValid()) {
        return null
    }

    // Use timelineStartDate if available and valid, otherwise use first data point
    const timelineStart = timelineStartDate
        ? dayjs.utc(timelineStartDate)
        : null
    const useTimelineStart = timelineStart && timelineStart.isValid()
    const effectiveStartDate = useTimelineStart ? timelineStart : firstDataDate

    // If no granularity, return full range from timelineStartDate to timelineEndDate (or data range)
    if (!granularity) {
        return {
            startDate: effectiveStartDate.toISOString(),
            endDate: targetEndDate.toISOString(),
        }
    }

    let desiredStartDate: dayjs.Dayjs
    switch (granularity) {
        case '1_day':
            // Subtract 1 day to get a full 24-hour period ending at targetEndDate
            desiredStartDate = targetEndDate.subtract(1, 'day')
            break
        case '1_week':
            // Subtract 1 week to get a full week of data
            desiredStartDate = targetEndDate.subtract(1, 'week')
            break
        case '1_month':
            // Subtract 1 calendar month to get a full month of data
            desiredStartDate = targetEndDate.subtract(1, 'month')
            break
        case '2_months':
            // Subtract 2 calendar months to get 2 months of data
            desiredStartDate = targetEndDate.subtract(2, 'month')
            break
        default:
            return {
                startDate: firstDataDate.toISOString(),
                endDate: targetEndDate.toISOString(),
            }
    }

    // Ensure start date is not before the effective start (timelineStartDate or first data point)
    const actualStartDate = desiredStartDate.isBefore(effectiveStartDate)
        ? effectiveStartDate
        : desiredStartDate
    const startWasClamped = actualStartDate.isAfter(desiredStartDate)

    // Find the index of the start date
    const startIndex = dateToIndex(actualStartDate.toISOString(), processedData)
    const endIndex = dateToIndex(targetEndDate.toISOString(), processedData)
    const dataPointCount = endIndex - startIndex + 1

    // Calculate the actual time span from start to end
    const actualTimeSpan = targetEndDate.diff(actualStartDate, 'millisecond')

    // Calculate expected time span for the granularity
    let expectedTimeSpan: number
    switch (granularity) {
        case '1_day':
            expectedTimeSpan = 24 * 60 * 60 * 1000 // 1 day in milliseconds
            break
        case '1_week':
            expectedTimeSpan = targetEndDate.diff(
                targetEndDate.subtract(1, 'week'),
                'millisecond'
            )
            break
        case '1_month':
            expectedTimeSpan = targetEndDate.diff(
                targetEndDate.subtract(1, 'month'),
                'millisecond'
            )
            break
        case '2_months':
            expectedTimeSpan = targetEndDate.diff(
                targetEndDate.subtract(2, 'month'),
                'millisecond'
            )
            break
        default:
            expectedTimeSpan = actualTimeSpan // No adjustment needed
    }

    // If there's insufficient data or the start was clamped, adjust end date to show full period
    let finalEndDate = targetEndDate
    const hasInsufficientTimeSpan = actualTimeSpan < expectedTimeSpan * 0.9 // Allow 10% tolerance

    // Skip end date adjustment if we're showing exactly 1 data point (startIndex === endIndex)
    // This happens when granularity is 1_day with discrete daily periods
    const isSingleDataPoint = startIndex === endIndex

    if (
        !isSingleDataPoint &&
        (startWasClamped || hasInsufficientTimeSpan || dataPointCount < 2)
    ) {
        // Calculate the desired end date to show a full granularity period from the actual start
        const desiredEndDateFromStart = actualStartDate.add(
            expectedTimeSpan,
            'millisecond'
        )

        // Determine the upper bound for the end date
        // Use timelineEndDate if provided, otherwise use the last data point
        const timelineEnd = timelineEndDate ? dayjs.utc(timelineEndDate) : null
        const lastDataDate = dayjs.utc(processedData[dataLength - 1]?.date)

        // Use desiredEndDateFromStart, but cap it at the timeline end if provided
        let candidateEndDate: dayjs.Dayjs
        if (
            timelineEnd &&
            timelineEnd.isValid() &&
            desiredEndDateFromStart.isAfter(lastDataDate)
        ) {
            // Desired end is beyond data range - use timeline end as upper bound
            candidateEndDate = desiredEndDateFromStart.isBefore(timelineEnd)
                ? desiredEndDateFromStart
                : timelineEnd
        } else {
            // Desired end is within data range - find the closest data point
            const desiredEndIndex = dateToIndex(
                desiredEndDateFromStart.toISOString(),
                processedData
            )
            const maxEndIndex = Math.min(dataLength - 1, desiredEndIndex)
            candidateEndDate = dayjs.utc(processedData[maxEndIndex]?.date)
        }

        if (
            candidateEndDate.isValid() &&
            candidateEndDate.isAfter(actualStartDate)
        ) {
            finalEndDate = candidateEndDate
        } else if (startIndex < dataLength - 1 && lastDataDate.isValid()) {
            // Fallback: use the last available data point
            if (lastDataDate.isAfter(actualStartDate)) {
                finalEndDate = lastDataDate
            }
        }
    }

    return {
        startDate: actualStartDate.toISOString(),
        endDate: finalEndDate.toISOString(),
    }
}

/**
 * Converts a date string to the closest index in the data array.
 * Returns the index of the first data point with a date >= targetDate.
 * If targetDate is before all data, returns 0.
 * If targetDate is after all data, returns dataLength - 1.
 */
export const dateToIndex = (
    date: string,
    processedData: DataEntry[]
): number => {
    const dataLength = processedData.length
    if (dataLength === 0) {
        return 0
    }

    const targetDate = dayjs.utc(date)
    if (!targetDate.isValid()) {
        return 0
    }

    const targetTimestamp = targetDate.valueOf()

    // Find the first index where date >= targetDate
    for (let i = 0; i < dataLength; i++) {
        const currentDate = dayjs.utc(processedData[i]?.date)
        if (currentDate.isValid() && currentDate.valueOf() >= targetTimestamp) {
            return i
        }
    }

    // If targetDate is after all data, return last index
    return dataLength - 1
}

/**
 * Converts an index to a date string from the data array.
 * Returns undefined if index is out of bounds or date is invalid.
 */
export const indexToDate = (
    index: number,
    processedData: DataEntry[]
): string | undefined => {
    if (index < 0 || index >= processedData.length) {
        return undefined
    }

    const date = dayjs.utc(processedData[index]?.date)
    if (!date.isValid()) {
        return undefined
    }

    return date.toISOString()
}
